<template>
  <div class="uploader-container" :class="{z1: isPopoverOpen}">
    <div class="image-upload" @click="openFileDialog">
      <div v-if="uploadProgress > 0" class="upload-progress">
        <circular-progress :percentage="uploadProgress"/>
      </div>
    </div>
    <input type="file" ref="fileInput" class="hidden" @change="handleFileChange" accept="image/*,video/*" multiple>
    <div class="upload-video video" v-show="!uploadType">
      <Popup v-if="!uploadType" @close="closeVideo">
        <EditLocalVideoForm v-if="isVideo" :videoUrl="selectedFile ? '' : previewImage" :videoFile="selectedFile" @close="closeVideoLocal" @save="handleVideoSave"/>
        <EditVideoForm v-else :editing="false" :video-url="videoUrl" @close="closeVideo"></EditVideoForm>
      </Popup>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, defineEmits, computed, onMounted, watch} from 'vue';
import axios, {type AxiosProgressEvent} from 'axios';
import Popover from '~/components/Popover.vue';
import RoundedIcon from '~/components/RoundedIcon.vue';
import CircularProgress from '~/components/CircularProgress.vue';
import { uploadMedia } from '~/api/mediaApi';

import * as pkg from 'vue-toastification';
const { useToast } = pkg;
import { defineProps } from 'vue';
import EditVideoForm from "~/components/works/EditVideoForm.vue";
import Popup from "~/components/Popup.vue";
import EditLocalVideoForm from "~/components/works/EditLocalVideoForm.vue";


const props = defineProps({
  existingImage: String,
  existingFile: Object,
  num: { type: Number, default: 0 },
});
const vimeoPreview = ref(false);
const isPopoverOpen = ref(false);
const isPopoverDisabled = ref(false);

const previewImage = ref<string | any>(props.existingImage || '');
const videoUrl = ref('');
const originalUrl = ref('');
const videoData = ref<any>(props.existingFile ? (props.existingFile.link ? props.existingFile : null) : null);
const uploadType = ref(true);
const fileInput = ref<any>(null);
const selectedFile = ref<any>(props.existingFile || null);
const uploadProgress = ref(0);
const isVideo = ref(props.existingFile ? props.existingFile.type.includes('video/') : false);

const emits = defineEmits(['imageSelected', 'multipleSelected', 'makeCover', 'menuToggled', 'uploadTypeChanged', 'progressChanged']);
const toast = useToast();

const showingMenuItems = computed(() => {
  return ['Upload item', 'Embed video'];
});

watch(() => props.existingFile, () => {
  isVideo.value = props.existingFile ? props.existingFile.type.includes('video') : (props.existingImage ? (props.existingImage.includes('.mp4') || props.existingImage.includes('.mov')) : false);
  console.log('file change', isVideo.value);
  selectedFile.value = props.existingFile;
  previewImage.value = props.existingImage;
});

watch(() => uploadType, () => {
  console.log('uploadTypeChanged', uploadType.value);
  emits('uploadTypeChanged', uploadType.value);
}, {immediate: true, deep: true});
watch(uploadProgress, () => {
  if (uploadProgress.value === 100) {
    emits('progressChanged', false);
    setTimeout(() => {
      uploadProgress.value = 0;
    },3000);
  } else {
    emits('progressChanged', true);
  }
});
const updatePopoverState = (newState: any) => {
  isPopoverOpen.value = newState;
};


const handleVideoSave = async (videoDetails: any) => {
  console.log('vid det', videoDetails);
  uploadType.value = true;
  const result = await uploadFile();
  const updatedVideoSrc = `${result}?start=${videoDetails.start}&end=${videoDetails.end}`;
  emits('imageSelected', 'video', updatedVideoSrc, props.num, null);
};

const closeVideo = (videoLink: string, videoDataValue: any) => {
  if (videoDataValue) {
    const pimage = videoDataValue.link;
    console.log('check 2', pimage, props.num, videoDataValue);
    emits('imageSelected', 'video', videoLink, props.num, videoDataValue);
  }
  uploadType.value = true;
};

const closeVideoLocal = (videoLink: string, videoDataValue: any) => {
  uploadType.value = true;
};

const fetchVideo = async () => {
  try {
    if (videoUrl.value) {

      const videoId = getVimeoVideoId(videoUrl.value);
      console.log('videoId', videoId, videoUrl.value);
      if (!videoId) {
        toast.error('Invalid Vimeo URL');
        return;
      }

      const response = await axios.get(`https://api.vimeo.com/videos/${videoId}`, {
        headers: {
          Authorization: `Bearer 2882f8d876e951dfb0357c88629dcf78` // Replace with your Vimeo access token
        }
      });

      videoData.value = response.data;
      previewImage.value = videoData.value.pictures.sizes[0].link;
      //emits('imageSelected', 'video', previewImage.value, props.num, videoData.value);
      uploadType.value = true;
      isPopoverOpen.value = false;
    }
    //toast.success('Video details fetched successfully');
  } catch (error) {
    toast.error('Failed to fetch video details');
    console.error('Vimeo API error:', error);
  }
};

const getVimeoVideoId = (url: any) => {
  const regex = /(?:vimeo\.com|vimeocdn\.com)\/(?:.*#|.*\/videos\/)?([0-9]+)(?:$|\/|\?)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const clearVideoUrl = () => {
  videoUrl.value = '';
  previewImage.value = '';
  uploadType.value = true;
  videoData.value = null;
};

const cancelVideoUrl = () => {
  videoUrl.value = '';
  uploadType.value = true;
  videoData.value = null;
};

const openFileDialog = () => {
  fileInput.value?.click();
  //console.log('check uploader', selectedFile)
};

import imageCompression from 'browser-image-compression';

// Function to compress image and return a compressed File
const compressImage = async (imageFile: File): Promise<File> => {
  const options = {
    maxSizeMB: 1, // Max size in MB
    maxWidthOrHeight: 1920, // Max dimension
    useWebWorker: true, // Use web worker for better performance
    initialQuality: 0.8 // Initial quality setting
  };
  try {
    // Compress the image file
    const compressedBlob = await imageCompression(imageFile, options);

    // Create a new File object from the compressed Blob, preserving original name and type
    const compressedFile = new File(
        [compressedBlob],
        imageFile.name, // Preserve the original filename
        { type: imageFile.type } // Preserve the original file type (e.g., "image/jpeg")
    );
    console.log('file comparison', compressedFile.size, imageFile.size);
    return compressedFile; // Return the compressed file
  } catch (error) {
    console.error('Error compressing image:', error);
    return imageFile; // Return original file if compression fails
  }
};

const handleFileChange = async (event: any) => {
  const files = event.target.files;
  if (files && files.length === 1) {
    const file = files[0];

    const maxSizeInBytes = 50 * 1024 * 1024;
    console.log('file size', file.size);
    const maxSizeInBytesVideo = 100 * 1024 * 1024;
    console.log('file size', file.size, maxSizeInBytes, file.size > maxSizeInBytes, file.type);
    const fileType = file.type.split('/')[0]; // Get the file type (e.g., "image", "video")

    let fileForSave = file;
    // Only apply the size check for image files
    if (fileType === 'image') {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50 MB. Please select a smaller image.');
        return;
      }
      fileForSave = await compressImage(file);

    } else if (fileType === 'video') {
      if (file.size > maxSizeInBytesVideo) {
        toast.error('File size exceeds 100 MB. Please select a smaller video.');
        return;
      }
      fileForSave = file;
    }

    selectedFile.value = fileForSave;
    isVideo.value = file.type.startsWith('video/');
    console.log('is vid', isVideo.value)
    const reader = new FileReader();
    reader.onload = (e: any) => {
      previewImage.value = e.target.result;

      if (isVideo.value) {
        uploadType.value = false;  // Open the video settings popup
      } else {
        emits('imageSelected', 'image', previewImage.value, props.num, selectedFile.value);
      }
    };
    reader.readAsDataURL(file);
  } else {
    emits('multipleSelected', props.num, files);
  }
};
const showVideoSettings = () => {
  uploadType.value = false;
  isPopoverOpen.value = false;
  console.log('show vid', selectedFile.value, uploadType.value);
};
const handleItemClick = (index: any) => {
  console.log('item click', !!previewImage.value, index, videoData.value)
  if (index === 'Upload item') {
    openFileDialog();
  } else if (index === 'Embed video') {
    isVideo.value = false;
    uploadType.value = false;
  } else {
    emits('imageSelected', 'remove', previewImage.value, props.num);
  }
  isPopoverOpen.value = false;
};
const uploadFile = async () => {
  console.log('data', videoData.value, selectedFile.value);
  if (videoData.value && !isVideo.value) {
    uploadProgress.value = 100;
    return Promise.resolve(videoData.value.link);
  }
  if (!selectedFile.value && !previewImage.value) {
    return Promise.reject('No file selected');
  } else if (!selectedFile.value) {
    uploadProgress.value = 100;
    return Promise.resolve(previewImage.value);
  }

  const formData = new FormData();
  formData.append('images', selectedFile.value);

  try {
    const uploadResponse = await uploadMedia(formData, (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.total) {
        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      }
    });
    const uploadedImageUrl = `https://api.artfield.app${uploadResponse.files[0]}`;
    previewImage.value = uploadedImageUrl;
    return Promise.resolve(uploadedImageUrl);
  } catch (error) {
    toast.error('Failed to upload file.');
    console.error('Upload error:', error);
    return Promise.reject('Upload failed');
  }
};
const uploadGivenFile = async (file: any) => {
  console.log('given data', file);
  const formData = new FormData();
  formData.append('images', file);

  try {
    const uploadResponse = await uploadMedia(formData, (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.total) {
        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      }
    });
    const uploadedImageUrl = `https://api.artfield.app${uploadResponse.files[0]}`;
    previewImage.value = uploadedImageUrl;
    return Promise.resolve(uploadedImageUrl);
  } catch (error) {
    toast.error('Failed to upload file.');
    console.error('Upload error:', error);
    return Promise.reject('Upload failed');
  }
};

defineExpose({ uploadFile, updatePopoverState, openFileDialog, handleItemClick });

onMounted(async() => {
  console.log('existing', props.existingImage);
  if (previewImage.value.includes('vimeo.com')) {
    isVideo.value = false;
    videoUrl.value = previewImage.value;
    await fetchVideo();
  } else {
    isVideo.value = props.existingFile ? props.existingFile.type.includes('video/') : (props.existingImage ? (props.existingImage.includes('.mp4') || props.existingImage.includes('.mov')) : false);
  }
  console.log('is vid', isVideo.value)
})
</script>

<style scoped>
.uploader-container {
  position: relative;
}
.uploader-container.z1 {
  z-index: 1;
}

.image-upload {
  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 10px;
}

.image-placeholder img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.image-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  font-size: 24px;
  color: #ccc;
  overflow: hidden;
}

.image-placeholder .placeholder {
  width: 40px;
  height: 40px;
}
.video-preview {
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 10px;
}

.options {
  position: absolute;
  top: 0;
  right: 0;
}

.options .icon {
  transform: scale(0.55);
}

.hidden {
  display: none;
}

.upload-video {
  display: flex;
  flex-direction: column;
  padding: 5px;
}

.upload-video button {
  background: #ededed;
  color: #000000;
  padding: 5px 20px;
  border-radius: 25px;
  font-size: 14px;
  margin-top: 10px;
}

.upload-video .confirm {
  background: #E3FF00;
}

.upload-video input {
  padding: 10px;
  border-radius: 10px;
  font-size: 14px;
}

.upload-progress {
  position: absolute;
  width: 30px;
  height: 30px;
  transform: translateY(-50%);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
}
</style>
